import { useMemo, useState } from "react";
import { NavLink } from "react-router-dom";
import { LogOut, Menu, Shield, ShieldCheck, ShieldPlus, UserRound } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { cn } from "@/lib/utils";

const roleLabel = (user) => {
  if (user?.is_admin_bypass) return "Dostęp wewnętrzny";
  if (user?.can_edit_questions) return "Zespół operacyjny";
  if (user?.can_review) return "Zespół operacyjny";
  return "Profil aplikanta";
};

const brandLabel = (user) => (user?.can_review ? "Byteflow | panel wewnętrzny" : "Byteflow");
const headerTitle = (user) => (user?.can_review ? "Panel zgłoszeń" : "Junior Dev Operative");
const headerKicker = (user) => (user?.can_review ? "Strefa wewnętrzna" : "Byteflow");

const navConfig = (user) => {
  if (user?.can_review) {
    return [
      { label: "Panel zgłoszeń", to: "/review", show: true },
      { label: "Pytania", to: "/questions", show: !!user?.can_edit_questions },
    ];
  }

  return [
    { label: "Formularz rekrutacyjny", to: "/apply", show: true },
  ];
};

const NavItems = ({ user, onNavigate }) => {
  const items = useMemo(() => navConfig(user).filter((item) => item.show), [user]);

  return (
    <nav className="space-y-1" aria-label="Main navigation">
      {items.map((item, index) => (
        <NavLink
          key={`${item.label}-${item.to}-${index}`}
          to={item.to}
          onClick={onNavigate}
          data-testid={`nav-link-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
          className={({ isActive }) =>
            cn(
              "flex items-center justify-between rounded-xl border border-transparent px-4 py-3 text-sm font-medium text-muted-foreground transition-colors duration-150 hover:border-border hover:bg-secondary hover:text-foreground",
              isActive && "border-border bg-secondary text-foreground shadow-sm",
            )
          }
        >
          <span>{item.label}</span>
          <span className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground/70">GO</span>
        </NavLink>
      ))}
    </nav>
  );
};

export const AppShell = ({ authData, onLogout, children }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const user = authData?.user;

  return (
    <div className="app-shell-grid bg-background text-foreground">
      <aside className="hidden border-r border-border bg-card/95 px-4 py-5 lg:block">
        <div className="surface-noise flex h-full flex-col rounded-3xl border border-border bg-card px-4 py-5 shadow-[0_10px_30px_rgba(0,0,0,0.35)]">
          <div className="mb-8 flex items-center gap-3">
            <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-primary/15 text-primary">
              <ShieldCheck className="h-6 w-6" />
            </div>
            <div>
              <p className="text-xs uppercase tracking-[0.28em] text-primary/70">{brandLabel(user)}</p>
              <h1 className="text-lg font-semibold">{headerTitle(user)}</h1>
            </div>
          </div>

          <div className="mb-6 rounded-2xl border border-primary/20 bg-primary/8 p-4">
            <div className="mb-2 flex items-center justify-between gap-2">
              <span className="text-xs uppercase tracking-[0.24em] text-primary/80">Sesja</span>
              <Badge className="border-primary/30 bg-primary/10 text-primary">Preview</Badge>
            </div>
            <p className="text-sm font-medium text-foreground">{user?.display_name}</p>
            <p className="mt-1 text-xs text-muted-foreground">{roleLabel(user)}</p>
          </div>

          <NavItems user={user} />

          <div className="mt-auto rounded-2xl border border-border bg-background/70 p-4">
            <div className="flex items-start gap-3">
              <div className="mt-0.5 rounded-xl bg-secondary p-2 text-primary">
                {user?.can_edit_questions ? <ShieldPlus className="h-4 w-4" /> : user?.can_review ? <Shield className="h-4 w-4" /> : <UserRound className="h-4 w-4" />}
              </div>
              <div>
                <p className="text-sm font-medium">Aktywny dostęp</p>
                <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
                  {user?.can_review
                    ? "Masz dostęp do wewnętrznych narzędzi obsługi zgłoszeń."
                    : "W tym miejscu uzupełnisz formularz i sprawdzisz status swojego zgłoszenia."}
                </p>
              </div>
            </div>
          </div>
        </div>
      </aside>

      <div className="min-w-0">
        <header className="sticky top-0 z-20 border-b border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/85">
          <div className="dashboard-header-strip px-4 py-4 sm:px-6 lg:px-10">
            <div className="mx-auto flex max-w-6xl items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
                  <SheetTrigger asChild>
                    <Button variant="outline" size="icon" className="lg:hidden" aria-label="Open navigation" data-testid="mobile-menu-button">
                      <Menu className="h-4 w-4" />
                    </Button>
                  </SheetTrigger>
                  <SheetContent side="left" className="border-border bg-card p-0">
                    <div className="flex h-full flex-col p-5">
                      <div className="mb-6 flex items-center gap-3">
                        <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-primary/15 text-primary">
                          <ShieldCheck className="h-5 w-5" />
                        </div>
                        <div>
                          <p className="text-xs uppercase tracking-[0.24em] text-primary/80">{brandLabel(user)}</p>
                          <h2 className="text-base font-semibold">Menu</h2>
                        </div>
                      </div>
                      <NavItems user={user} onNavigate={() => setMobileOpen(false)} />
                    </div>
                  </SheetContent>
                </Sheet>
                <div>
                  <p className="text-xs uppercase tracking-[0.28em] text-primary/80">{headerKicker(user)}</p>
                  <h2 className="text-lg font-semibold sm:text-xl">{headerTitle(user)}</h2>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <div className="hidden rounded-2xl border border-border bg-card px-4 py-2 sm:block">
                  <p className="text-xs text-muted-foreground">Aktywna sesja</p>
                  <p className="text-sm font-medium">{user?.display_name}</p>
                </div>
                <Badge className="border-border bg-secondary px-3 py-1 text-foreground">{roleLabel(user)}</Badge>
                <Button variant="outline" onClick={onLogout} data-testid="logout-button">
                  <LogOut className="mr-2 h-4 w-4" /> Wyloguj
                </Button>
              </div>
            </div>
          </div>
        </header>

        <main className="px-4 py-6 sm:px-6 lg:px-10 lg:py-8">
          <div className="mx-auto max-w-6xl space-y-4">{children}</div>
        </main>
      </div>
    </div>
  );
};
